import {
  SettingsPermissions,
  SupportPermissions,
} from "@redotech/account-common/permissions";
import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type { FolderResponse } from "@redotech/effect-api-shared/rpc/view-folder";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import EditPencilIcon from "@redotech/redo-icons/edit-01.svg";
import FolderOpenIcon from "@redotech/redo-icons/folder-open.svg";
import FolderIcon from "@redotech/redo-icons/folder.svg";
import QuestionCircleIcon from "@redotech/redo-icons/help-circle.svg";
import TicketIcon from "@redotech/redo-icons/ticket-02.svg";
import TicketFilledIcon from "@redotech/redo-icons/ticket-02_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  ConversationCountsContext,
  ConversationCountsService,
} from "@redotech/redo-merchant-support-app/services/conversation-counts-service";
import { SupportViewOrderingContext } from "@redotech/redo-merchant-support-app/services/support-view-ordering-service";
import { ViewInfo } from "@redotech/redo-merchant-support-app/views";
import type { SupportFolder as SupportFolderType } from "@redotech/redo-model/support-folder";
import {
  AI_CONVERSATIONS_VIEW_FILTER_GROUP,
  ARCHIVED_VIEW_FILTER_GROUP,
  buildAssignedViewFilterGroup,
  buildMentionsViewFilterGroup,
  DEFAULT_VIEW_DISPLAY_NAMES,
  DefaultView,
  DRAFTS_VIEW_FILTER_GROUP,
  isDefaultView,
  OPEN_VIEW_FILTER_GROUP,
  SENT_VIEW_FILTER_GROUP,
  SPAM_VIEW_FILTER_GROUP,
  withSpamTagExclusion,
} from "@redotech/redo-support-v2/query/default-views";
import { orderSupportNavEntries } from "@redotech/redo-support-v2/query/support-ticket-nav-ordering";
import type { FilterGroup } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { encodeAdvancedFilterGroup } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import {
  RedoNavDraggableList,
  RedoNavDraggableListItem,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-nav-draggable-list";
import { RedoNavFolder } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-nav-folder";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, type ReactNode, useMemo, useState } from "react";
import { useLocation, useMatch } from "react-router-dom";
import {
  SUPPORT_ICON,
  SUPPORT_ICON_FILLED,
  SupportSuiteStatusesContext,
} from "../../../../redo-suite/support-cloud";
import { userNameCache } from "../../../../support-v2/filters/assignees-filter";
import { useSupportTicketCounts } from "../../../../support-v2/hooks/use-support-ticket-counts";
import { useSupportV2FolderActions } from "../../../../support-v2/hooks/use-support-v2-folder-actions";
import { useSupportV2UserOrdering } from "../../../../support-v2/hooks/use-support-v2-user-ordering";
import { useSupportV2ViewsAndFolders } from "../../../../support-v2/hooks/use-support-v2-views-and-folders";
import { SupportV2FolderModal } from "../../../../support-v2/views/folders/support-v2-folder-modal";
import { SupportAnalyticsItems } from "./analytics";
import { ConciergeItems } from "./concierge";
import { DevItems } from "./dev";
import { useSupportNotifications } from "./support-notifications";
import { useHasAnyOpenConversations } from "./use-has-any-open-conversations";

export function useSupportNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { supportEnabled } = useRequiredContext(SupportSuiteStatusesContext);

  const canViewSupport = useHasPermission(SupportPermissions.VIEW_SUPPORT);

  useSupportNotifications();

  const hasAnyNonZeroCounts = useHasAnyOpenConversations();

  if (!canViewSupport) {
    return null;
  }

  const baseUrl = routes.legacySupportBaseUrl;
  const primaryUrl = routes.getSupportHref({ enabled: supportEnabled });

  const primaryTab: LinkPrimaryTab = {
    id: "support",
    hasNotification: hasAnyNonZeroCounts,
    UnfilledIcon: SUPPORT_ICON,
    FilledIcon: SUPPORT_ICON_FILLED,
    type: "link",
    pathRoot: baseUrl,
    href: primaryUrl,
    friendlyName: "Support",
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "support",
    name: "Support",
    NavContent: SecondarySupportSection,
  };

  return {
    primaryTab,
    secondaryNavigationData,
    isActiveProduct: supportEnabled,
  };
}

const SecondarySupportSection = memo(function SecondarySupportSection() {
  const { id: nodeId, TreeContext } = useNavTreeNode({
    friendlyName: "Support",
  });

  const matchSupportV2Wildcard = !!useMatch("/stores/:storeId/support-v2/*");
  const matchSupportV2Root = !!useMatch("/stores/:storeId/support-v2");
  const isOnSupportV2 = matchSupportV2Wildcard || matchSupportV2Root;

  return (
    <TreeContext.Provider value={nodeId}>
      <SupportAnalyticsItems />
      <ConciergeItems />
      <DevItems />
      <FaqItems />
      {isOnSupportV2 ? (
        <SupportV2TicketsItem />
      ) : (
        <ConversationCountsService>
          <SupportTicketsItem />
        </ConversationCountsService>
      )}
    </TreeContext.Provider>
  );
});

const SupportV2TicketsItem = memo(function SupportV2TicketsItem() {
  return <SupportV2TicketsItemContent />;
});

function buildDefaultViewHref(
  teamId: string,
  defaultView: DefaultView,
  filterGroup: FilterGroup,
): string {
  const base = `/stores/${teamId}/support-v2/`;
  const params = new URLSearchParams();
  params.set("defaultView", defaultView);
  try {
    const encoded = encodeAdvancedFilterGroup(filterGroup);
    params.set("fg", encoded);
  } catch {
    // ignore encoding errors
  }
  return `${base}?${params.toString()}`;
}

function buildViewHref(
  teamId: string,
  viewId: string,
  filterGroup: unknown,
): string {
  const base = `/stores/${teamId}/support-v2/`;
  const params = new URLSearchParams();
  params.set("view", viewId);
  const effectiveFilterGroup = withSpamTagExclusion(
    (filterGroup as FilterGroup | null) ?? null,
  );
  try {
    const encoded = encodeAdvancedFilterGroup(effectiveFilterGroup);
    params.set("fg", encoded);
  } catch {
    // ignore encoding errors
  }
  return `${base}?${params.toString()}`;
}

function getDefaultViewFilterGroup(
  view: DefaultView,
  assignedFilterGroup: FilterGroup | null,
  mentionsFilterGroup: FilterGroup,
): FilterGroup {
  switch (view) {
    case DefaultView.OPEN:
      return OPEN_VIEW_FILTER_GROUP;
    case DefaultView.ASSIGNED:
      return assignedFilterGroup ?? OPEN_VIEW_FILTER_GROUP;
    case DefaultView.MENTIONS:
      return mentionsFilterGroup;
    case DefaultView.DRAFTS:
      return DRAFTS_VIEW_FILTER_GROUP;
    case DefaultView.AI_CONVERSATIONS:
      return AI_CONVERSATIONS_VIEW_FILTER_GROUP;
    case DefaultView.SENT:
      return SENT_VIEW_FILTER_GROUP;
    case DefaultView.SPAM:
      return SPAM_VIEW_FILTER_GROUP;
    case DefaultView.ARCHIVED:
      return ARCHIVED_VIEW_FILTER_GROUP;
  }
}

// Default views shown above custom views / folders before any user reorder.
const TOP_DEFAULT_VIEWS: DefaultView[] = [
  DefaultView.OPEN,
  DefaultView.ASSIGNED,
  DefaultView.MENTIONS,
  DefaultView.DRAFTS,
  DefaultView.AI_CONVERSATIONS,
];
// Default views shown below custom views / folders before any user reorder.
const BOTTOM_DEFAULT_VIEWS: DefaultView[] = [
  DefaultView.SENT,
  DefaultView.SPAM,
  DefaultView.ARCHIVED,
];
// Default views that surface an unread count at the top level.
const DEFAULT_VIEWS_WITH_COUNT: ReadonlySet<DefaultView> = new Set([
  DefaultView.OPEN,
  DefaultView.ASSIGNED,
  DefaultView.MENTIONS,
  DefaultView.DRAFTS,
]);

// A reorderable top-level nav entry, persisted as `{ id, type }` ordering.
type SupportNavEntry =
  | { kind: "defaultView"; id: DefaultView }
  | { kind: "view"; id: string }
  | { kind: "folder"; id: string };

const SupportV2TicketsItemContent = memo(
  function SupportV2TicketsItemContent() {
    const team = useRequiredContext(TeamContext);
    const location = useLocation();
    const user = useRequiredContext(UserContext);
    const { views, folders } = useSupportV2ViewsAndFolders();
    const { createFolder, updateFolder, deleteFolder } =
      useSupportV2FolderActions();
    const countsMap = useSupportTicketCounts(views);
    const { ordering, saveOrdering } = useSupportV2UserOrdering();

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [folderBeingEdited, setFolderBeingEdited] =
      useState<FolderResponse | null>(null);

    const reservedFolderNames = useMemo(
      () => new Set(folders.map((f) => f.name)),
      [folders],
    );

    const folderlessViews = useMemo(
      () => views.filter((v) => !v.folderId),
      [views],
    );

    const matchChildPath = !!useMatch(`/stores/${team._id}/support-v2/*`);
    const matchRootPath = !!useMatch(`/stores/${team._id}/support-v2`);
    const anyChildSelected = matchChildPath || matchRootPath;

    const currentSearchParams = new URLSearchParams(location.search);
    const currentViewId = currentSearchParams.get("view");
    const currentDefaultView = currentSearchParams.get("defaultView");

    const assignedFilterGroup = useMemo(() => {
      if (user.name) {
        userNameCache.set(MongoUserId.fromString(user._id), user.name);
      }
      return buildAssignedViewFilterGroup(user._id);
    }, [user?._id, user?.name]);

    const mentionsFilterGroup = useMemo(
      () => buildMentionsViewFilterGroup(user._id),
      [user?._id],
    );

    // Map from DefaultView id → the folder that contains it (for deduplication)
    const folderDefaultViewsMap = useMemo(() => {
      const map = new Map<string, FolderResponse>();
      for (const folder of folders) {
        for (const dv of folder.defaultViews) {
          map.set(dv, folder);
        }
      }
      return map;
    }, [folders]);

    // All views for the folder modal: default views + custom views
    const allViewsForModal = useMemo(
      () => [
        ...Object.values(DefaultView).map((dv) => ({
          id: dv,
          name: DEFAULT_VIEW_DISPLAY_NAMES[dv],
        })),
        ...views.map((v) => ({ id: String(v.id), name: v.name })),
      ],
      [views],
    );

    // Single reorderable list: folderless default views, folders, and
    // folderless custom views, sorted by the user's saved ordering.
    const navEntries: RedoNavDraggableListItem<SupportNavEntry>[] =
      useMemo(() => {
        const itemByKey = new Map<
          string,
          RedoNavDraggableListItem<SupportNavEntry>
        >();

        for (const dv of [...TOP_DEFAULT_VIEWS, ...BOTTOM_DEFAULT_VIEWS]) {
          if (folderDefaultViewsMap.has(dv)) {
            continue;
          }
          itemByKey.set(`defaultView:${dv}`, {
            id: dv,
            friendlyName: DEFAULT_VIEW_DISPLAY_NAMES[dv],
            notificationCount: DEFAULT_VIEWS_WITH_COUNT.has(dv)
              ? (countsMap.get(dv) ?? 0)
              : undefined,
            selected: currentDefaultView === dv,
            action: {
              type: "link" as const,
              href: buildDefaultViewHref(
                team._id,
                dv,
                getDefaultViewFilterGroup(
                  dv,
                  assignedFilterGroup,
                  mentionsFilterGroup,
                ),
              ),
            },
            itemValue: { kind: "defaultView", id: dv },
          });
        }

        for (const folder of folders) {
          const folderViews = views.filter(
            (v) => String(v.folderId) === String(folder.id),
          );
          const folderDefaultViews = folder.defaultViews.filter(isDefaultView);
          const anyChildSelected =
            folderViews.some((v) => currentViewId === String(v.id)) ||
            (currentDefaultView != null &&
              isDefaultView(currentDefaultView) &&
              folderDefaultViews.includes(currentDefaultView));
          const folderChildren: ReactNode[] = [
            ...folderDefaultViews.map((dv) => (
              <RedoSecondaryNavItem
                action={{
                  type: "link",
                  href: buildDefaultViewHref(
                    team._id,
                    dv,
                    getDefaultViewFilterGroup(
                      dv,
                      assignedFilterGroup,
                      mentionsFilterGroup,
                    ),
                  ),
                }}
                friendlyName={DEFAULT_VIEW_DISPLAY_NAMES[dv]}
                id={dv}
                key={dv}
                notificationCount={countsMap.get(dv) ?? 0}
                selected={currentDefaultView === dv}
              />
            )),
            ...folderViews.map((view) => (
              <RedoSecondaryNavItem
                action={{
                  type: "link",
                  href: buildViewHref(
                    team._id,
                    String(view.id),
                    view.filterGroup,
                  ),
                }}
                friendlyName={view.name}
                id={String(view.id)}
                key={String(view.id)}
                notificationCount={countsMap.get(String(view.id)) ?? 0}
                selected={currentViewId === String(view.id)}
              />
            )),
            <RedoSecondaryNavItem
              action={{
                type: "button",
                onClick: () => setFolderBeingEdited(folder),
              }}
              FilledIcon={EditPencilIcon}
              friendlyName="Edit folder"
              id="edit-folder"
              key="edit-folder"
              UnfilledIcon={EditPencilIcon}
            />,
          ];
          itemByKey.set(`folder:${String(folder.id)}`, {
            id: String(folder.id),
            friendlyName: folder.name,
            isFolder: true,
            UnfilledIcon: FolderIcon,
            FilledIcon: FolderOpenIcon,
            anyChildSelected,
            action: { type: "button" },
            children: folderChildren,
            itemValue: { kind: "folder", id: String(folder.id) },
          });
        }

        for (const view of folderlessViews) {
          itemByKey.set(`view:${String(view.id)}`, {
            id: String(view.id),
            friendlyName: view.name,
            notificationCount: countsMap.get(String(view.id)) ?? 0,
            selected: currentViewId === String(view.id),
            action: {
              type: "link" as const,
              href: buildViewHref(team._id, String(view.id), view.filterGroup),
            },
            itemValue: { kind: "view", id: String(view.id) },
          });
        }

        const orderedRefs = orderSupportNavEntries({
          ordering,
          topDefaultViewIds: TOP_DEFAULT_VIEWS,
          bottomDefaultViewIds: BOTTOM_DEFAULT_VIEWS,
          folderedDefaultViewIds: new Set(folderDefaultViewsMap.keys()),
          folders: folders.map((f) => ({ id: String(f.id) })),
          folderlessViews: folderlessViews.map((v) => ({
            id: String(v.id),
            position: v.position,
          })),
        });

        return orderedRefs.flatMap((ref) => {
          const item = itemByKey.get(`${ref.kind}:${ref.id}`);
          return item ? [item] : [];
        });
      }, [
        ordering,
        folders,
        views,
        folderlessViews,
        folderDefaultViewsMap,
        countsMap,
        team._id,
        currentViewId,
        currentDefaultView,
        assignedFilterGroup,
        mentionsFilterGroup,
      ]);

    const handleReorder = useHandler(
      (newOrder: RedoNavDraggableListItem<SupportNavEntry>[]) => {
        void saveOrdering(
          newOrder.map((item) => ({
            id: item.itemValue.id,
            type: item.itemValue.kind === "folder" ? "folder" : "view",
          })),
        );
      },
    );

    return (
      <>
        <RedoSecondaryNavItem
          action={{ type: "link", href: `/stores/${team._id}/support-v2/` }}
          anyChildSelected={anyChildSelected}
          FilledIcon={TicketFilledIcon}
          friendlyName="Tickets"
          id="tickets"
          UnfilledIcon={TicketIcon}
        >
          {/* Default views, folders, and custom views in one reorderable list */}
          <RedoNavDraggableList
            draggableItems={navEntries}
            setDraggableItems={handleReorder}
          />

          <RedoSecondaryNavItem
            action={{
              type: "button",
              onClick: () => setIsCreateModalOpen(true),
            }}
            friendlyName="+ Create folder"
            id="create-folder"
          />
        </RedoSecondaryNavItem>

        <SupportV2FolderModal
          currentName=""
          currentViewIds={[]}
          isOpen={isCreateModalOpen}
          mode="create"
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={async (name, selectedViewIds, isPrivate) => {
            const defaultViewIds = selectedViewIds.filter(isDefaultView);
            const customViewIds = selectedViewIds.filter(
              (id) => !isDefaultView(id),
            );
            await createFolder(name, customViewIds, isPrivate, defaultViewIds);
            setIsCreateModalOpen(false);
          }}
          reservedFolderNames={reservedFolderNames}
          views={allViewsForModal}
        />

        {folderBeingEdited && (
          <SupportV2FolderModal
            currentName={folderBeingEdited.name}
            currentViewIds={[
              ...folderBeingEdited.defaultViews.filter(isDefaultView),
              ...views
                .filter(
                  (v) =>
                    v.folderId != null &&
                    String(v.folderId) === String(folderBeingEdited.id),
                )
                .map((v) => String(v.id)),
            ]}
            isOpen
            mode="edit"
            onClose={() => setFolderBeingEdited(null)}
            onDelete={async () => {
              await deleteFolder(String(folderBeingEdited.id));
              setFolderBeingEdited(null);
            }}
            onSubmit={async (name, selectedViewIds) => {
              const defaultViewIds = selectedViewIds.filter(isDefaultView);
              const customViewIds = selectedViewIds.filter(
                (id) => !isDefaultView(id),
              );
              await updateFolder(
                String(folderBeingEdited.id),
                name,
                customViewIds,
                views.map((v) => ({
                  id: String(v.id),
                  folderId: v.folderId ? String(v.folderId) : null,
                })),
                defaultViewIds,
              );
              setFolderBeingEdited(null);
            }}
            reservedFolderNames={reservedFolderNames}
            views={allViewsForModal}
          />
        )}
      </>
    );
  },
);

const SupportTicketsItem = memo(function SupportTicketsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { supportEnabled } = useRequiredContext(SupportSuiteStatusesContext);
  const conversationCounts = useRequiredContext(ConversationCountsContext);

  const {
    folderlessViewOrdering,
    setFolderBeingEdited,
    folders,
    updateViewOrdering,
    openCreateFolderModal,
  } = useRequiredContext(SupportViewOrderingContext);

  const reorderableViews: RedoNavDraggableListItem<ViewInfo>[] = useMemo(() => {
    const itemToProps = (
      view: ViewInfo,
    ): RedoNavDraggableListItem<ViewInfo> => {
      const viewNameToUse = view.identifier.name || view.title;
      const viewCountKey = view.identifier?._id
        ? view.title
        : view.identifier?.name || "";

      const unreadCount = conversationCounts[viewCountKey] ?? 0;

      return {
        id: view.identifier._id || view.identifier.name || "",
        friendlyName: view.title,
        notificationCount: unreadCount,
        itemValue: view,
        isPrivate: view.isPrivate,
        muted: view.muted,
        action: {
          type: "link",
          href: routes.getSupportTableHref(viewNameToUse),
        },
      };
    };

    return folderlessViewOrdering.map(itemToProps);
  }, [conversationCounts, folderlessViewOrdering, routes]);

  const legacySupportBaseUrl = routes.legacySupportBaseUrl;
  const anyChildSelected = useMatch(`${legacySupportBaseUrl}/*`);

  if (!supportEnabled) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={TicketFilledIcon}
      friendlyName="Tickets"
      id="tickets"
      UnfilledIcon={TicketIcon}
    >
      {folders.map((folder) => (
        <SupportFolder
          folder={folder}
          folderEditRequested={() => setFolderBeingEdited(folder)}
          key={folder._id}
        />
      ))}
      <RedoNavDraggableList
        draggableItems={reorderableViews}
        setDraggableItems={(newOrder) =>
          updateViewOrdering(newOrder.map((item) => item.itemValue))
        }
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.getSupportTableSubRoute("sent") }}
        friendlyName="Sent"
        id="view-sent"
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.getSupportTableSubRoute("spam") }}
        friendlyName="Spam"
        id="view-spam"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getSupportTableSubRoute("create"),
        }}
        friendlyName="+ Add new view"
        id="create-view"
      />
      <RedoSecondaryNavItem
        action={{ type: "button", onClick: openCreateFolderModal }}
        friendlyName="+ Create folder"
        id="create-folder"
      />
    </RedoSecondaryNavItem>
  );
});

const SupportFolder = memo(function SupportFolder({
  folder,
  folderEditRequested,
}: {
  folder: SupportFolderType;
  folderEditRequested: () => void;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  const { defaultViewOrdering } = useRequiredContext(
    SupportViewOrderingContext,
  );

  const conversationCounts = useRequiredContext(ConversationCountsContext);

  const userDefinedViewsInFolder = useMemo(() => {
    const viewIds = new Set(folder.viewIds);

    return defaultViewOrdering.filter((view) =>
      view.identifier._id ? viewIds.has(view.identifier._id) : false,
    );
  }, [folder.viewIds, defaultViewOrdering]);

  const defaultViewsInFolder = useMemo(() => {
    const viewNames = new Set(folder.defaultViews);

    return defaultViewOrdering.filter((view) =>
      view.identifier.name ? viewNames.has(view.identifier.name) : false,
    );
  }, [folder.defaultViews, defaultViewOrdering]);

  const allFolderViews = useMemo(() => {
    return [...userDefinedViewsInFolder, ...defaultViewsInFolder];
  }, [userDefinedViewsInFolder, defaultViewsInFolder]);

  const location = useLocation();

  const anyChildSelected = useMemo(() => {
    return allFolderViews.some((view) => {
      const viewNameToUse = view.identifier.name || view.title;
      const link = routes.getSupportTableHref(viewNameToUse);
      return location.pathname.startsWith(link);
    });
  }, [location.pathname, allFolderViews, routes]);

  const folderChildren = allFolderViews.map((view) => {
    const uniqueId = view.identifier._id || view.identifier.name || "";

    const linkName = view.identifier.name || view.title;

    const link = routes.getSupportTableHref(linkName);

    const viewCountKey = view.identifier?._id
      ? view.title
      : view.identifier?.name || "";

    const unreadCount = conversationCounts[viewCountKey] ?? 0;

    return (
      <RedoSecondaryNavItem
        action={{ type: "link", href: link }}
        friendlyName={view.title}
        id={uniqueId}
        isPrivate={view.isPrivate}
        key={uniqueId}
        muted={view.muted}
        notificationCount={unreadCount}
      />
    );
  });

  return (
    <RedoNavFolder
      anyChildSelected={anyChildSelected}
      folderEditRequested={folderEditRequested}
      folderId={folder._id}
      folderName={folder.name}
    >
      {folderChildren}
    </RedoNavFolder>
  );
});

const FaqItems = memo(function FaqItems() {
  const routes = useRequiredContext(MerchantRoutesContext);

  const canManageFaq = useHasPermission(
    SettingsPermissions.SET_SUPPORT_SETTINGS,
  );

  const legacySupportBaseUrl = routes.legacySupportBaseUrl;
  const anyChildSelected = useMatch(`${legacySupportBaseUrl}/faq/*`);

  if (!canManageFaq) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={QuestionCircleIcon}
      friendlyName="FAQ"
      id="faq"
      UnfilledIcon={QuestionCircleIcon}
    >
      <RedoSecondaryNavItem
        action={{ type: "link", href: `${legacySupportBaseUrl}/faq/home` }}
        friendlyName="Content"
        id="faq-collections-and-articles"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${legacySupportBaseUrl}/faq/editor/theme`,
        }}
        friendlyName="Style"
        id="faq-theme-editor"
      />
    </RedoSecondaryNavItem>
  );
});
